import api from './api';

/**
 * @class FavoritesService
 * @description Service for managing user favorites functionality
 */
class FavoritesService {
  /**
   * Get user's favorite properties
   * @returns {Promise<Object>} API response with favorites data
   */
  static async getFavorites() {
    try {
      const response = await api.get('/favorites');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add property to favorites
   * @param {string} propertyId - Property ID to add to favorites
   * @returns {Promise<Object>} API response
   */
  static async addToFavorites(propertyId) {
    try {
      const response = await api.post(`/favorites/${propertyId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove property from favorites
   * @param {string} propertyId - Property ID to remove from favorites
   * @returns {Promise<Object>} API response
   */
  static async removeFromFavorites(propertyId) {
    try {
      const response = await api.delete(`/favorites/${propertyId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Check if property is favorited
   * @param {string} propertyId - Property ID to check
   * @returns {Promise<Object>} API response with favorite status
   */
  static async checkFavorite(propertyId) {
    try {
      const response = await api.get(`/favorites/check/${propertyId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get favorites count
   * @returns {Promise<Object>} API response with favorites count
   */
  static async getFavoritesCount() {
    try {
      const response = await api.get('/favorites/count');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Toggle favorite status of a property
   * @param {string} propertyId - Property ID to toggle
   * @param {boolean} isFavorited - Current favorite status
   * @returns {Promise<Object>} API response
   */
  static async toggleFavorite(propertyId, isFavorited) {
    try {
      if (isFavorited) {
        return await this.removeFromFavorites(propertyId);
      } else {
        return await this.addToFavorites(propertyId);
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Object} error - Error object from API
   * @returns {Error} Formatted error
   */
  static handleError(error) {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || 'An error occurred with favorites';
      const statusCode = error.response.status;
      
      const customError = new Error(message);
      customError.statusCode = statusCode;
      customError.data = error.response.data;
      
      return customError;
    } else if (error.request) {
      // Request was made but no response received
      return new Error('Network error. Please check your connection.');
    } else {
      // Something else happened
      return new Error(error.message || 'An unexpected error occurred');
    }
  }

  /**
   * Get favorites from local storage (backup)
   * @returns {Array} Array of property IDs
   */
  static getFavoritesFromStorage() {
    try {
      const favorites = localStorage.getItem('userFavorites');
      return favorites ? JSON.parse(favorites) : [];
    } catch (error) {
      console.error('Error reading favorites from storage:', error);
      return [];
    }
  }

  /**
   * Save favorites to local storage (backup)
   * @param {Array} favorites - Array of property IDs
   */
  static saveFavoritesToStorage(favorites) {
    try {
      localStorage.setItem('userFavorites', JSON.stringify(favorites));
    } catch (error) {
      console.error('Error saving favorites to storage:', error);
    }
  }

  /**
   * Clear favorites from local storage
   */
  static clearFavoritesFromStorage() {
    try {
      localStorage.removeItem('userFavorites');
    } catch (error) {
      console.error('Error clearing favorites from storage:', error);
    }
  }
}

export default FavoritesService; 